/**
 * Builds a name comparator for the station's language.
 *
 * `Intl.Collator` is the fast path: it is built once per locale rather than
 * per comparison, and it is the only form that actually honours locale rules.
 * `numeric: true` keeps `Printer 2` ahead of `Printer 10`, which matters
 * because hardware is usually named with a trailing number.
 *
 * Hermes can ship without ICU and this app bundles no Intl polyfill, so the
 * fallback matters: a bare `localeCompare` still yields a stable, sensible
 * order, just in the runtime's default locale rather than the station's.
 */
export const createNameComparator = (
  localeCode?: string,
): ((a: string, b: string) => number) => {
  if (typeof Intl !== 'undefined' && typeof Intl.Collator === 'function') {
    try {
      const { compare } = new Intl.Collator(localeCode, { numeric: true });
      return compare;
    } catch (e) {
      // An unsupported locale tag throws; fall through to the plain compare.
    }
  }

  return (a: string, b: string) => a.localeCompare(b);
};

/**
 * Sorts a copy by name. Callers routinely hold arrays owned by the Apollo
 * cache or by session state, and `Array.prototype.sort` mutates in place.
 */
export const sortByName = <T extends { name: string }>(
  items: T[],
  compare: (a: string, b: string) => number,
): T[] => [...items].sort((a, b) => compare(a.name, b.name));
